import React from 'react';
import i18n from 'util/i18n';

import { Summary, DateSummary } from 'components/Summary';
import { SUBSTANCE_USE, SUBSTANCE_USE_CONTROLLED_ILLEGAL_USAGE } from 'config/formSections/substanceUse';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { drugsSupplementalTreatmentAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import DrugSupplementalUse from './DrugSupplementalUse';

const sectionConfig = {
  key: SUBSTANCE_USE_CONTROLLED_ILLEGAL_USAGE.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_CONTROLLED_ILLEGAL_USAGE.name,
  storeKey: SUBSTANCE_USE_CONTROLLED_ILLEGAL_USAGE.storeKey,
};

export class DrugSupplementalUses extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateIllegalControlledSubstanceUseAnswer = this.updateIllegalControlledSubstanceUseAnswer.bind(this);
    this.updateBranchComment = this.updateBranchComment.bind(this);
    this.updateSummaryComment = this.updateSummaryComment.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      IllegalControlledSubstanceUseAnswer: this.props.IllegalControlledSubstanceUseAnswer,
      IllegalControlledSubstanceUseComment: this.props.IllegalControlledSubstanceUseComment,
      IllegalControlledSubstanceUseSummaryComment: this.props.IllegalControlledSubstanceUseSummaryComment,
      List: this.props.List,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateIllegalControlledSubstanceUseAnswer = (values) => {
    const additional = values.value === 'Yes' ? {} : {
      List: [],
      IllegalControlledSubstanceUseSummaryComment: ''
    };
    this.update({
      IllegalControlledSubstanceUseAnswer: values,
      ...additional
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const range = DateSummary(o.DateRange);
    const drugUsed = (o.DrugUsed || {}).value;

    return Summary({
      type: i18n.t('substance.drugs.supplementalUsage.collection.itemType'),
      index,
      left: drugUsed,
      right: range,
      placeholder: i18n.t('substance.drugs.supplementalUsage.collection.summary'),
    });
  };

  updateBranchComment = (values) => {
    this.update({
      IllegalControlledSubstanceUseComment: values,
    });
  };

  updateSummaryComment = (values) => {
    this.update({
      IllegalControlledSubstanceUseSummaryComment: values,
    });
  };

  render() {
    const {
      errors, peopleWhoKnowYou, List
    } = this.props;

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(
      List?.items,
      peopleWhoKnowYou,
      drugsSupplementalTreatmentAddressNameFields
    );
    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div
        className="section-content drug-supplemental-uses"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_CONTROLLED_ILLEGAL_USAGE.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.drugs.supplementalUsage')}</h1>
        <Branch
          attribute="IllegalControlledSubstanceUseAnswer"
          name="IllegalControlledSubstanceUseAnswer"
          label={i18n.t('substance.pvq.drugs.heading.supplementalUsage')}
          labelSize="h4"
          className="illegal-controlled-substance-use-answer"
          {...this.props.IllegalControlledSubstanceUseAnswer}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateIllegalControlledSubstanceUseAnswer}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={this.props.IllegalControlledSubstanceUseAnswer.value === 'Yes'}>
          <Accordion
            attribute="List"
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.pvq.drugs.supplementalUsage.collection.description')}
            appendTitle={i18n.t('substance.pvq.drugs.supplementalUsage.collection.appendTitle')}
            appendLabel={i18n.t('substance.pvq.drugs.supplementalUsage.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <DrugSupplementalUse
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
            />
          </Accordion>
        </Show>
      </div>

    ) : (
      <div
        className="section-content drug-supplemental-uses"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_CONTROLLED_ILLEGAL_USAGE.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.drugs.supplementalUsage')}</h1>
        <Branch
          attribute="IllegalControlledSubstanceUseAnswer"
          name="IllegalControlledSubstanceUseAnswer"
          label={i18n.t('substance.drugs.heading.supplementalUsage')}
          labelSize="h4"
          className="illegal-controlled-substance-use-answer"
          {...this.props.IllegalControlledSubstanceUseAnswer}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateIllegalControlledSubstanceUseAnswer}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('substance.drugs.supplementalUsage.heading.branchComment')}
          name="IllegalControlledSubstanceUseComment"
          className="illegal-controlled-substance-use-comment"
          attribute="IllegalControlledSubstanceUseComment"
          {...this.props.IllegalControlledSubstanceUseComment}
          onUpdate={this.updateBranchComment}
          required={false}
        />
        <Show when={this.props.IllegalControlledSubstanceUseAnswer.value === 'Yes'}>
          <Accordion
            attribute="List"
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.drugs.supplementalUsage.collection.description')}
            appendTitle={i18n.t('substance.drugs.supplementalUsage.collection.appendTitle')}
            appendLabel={i18n.t('substance.drugs.supplementalUsage.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <DrugSupplementalUse
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.drugs.supplementalUsage.heading.summaryComment')}
            name="IllegalControlledSubstanceUseSummaryComment"
            className="illegal-controlled-substance-use-summary-comment"
            attribute="IllegalControlledSubstanceUseSummaryComment"
            {...this.props.IllegalControlledSubstanceUseSummaryComment}
            onUpdate={this.updateSummaryComment}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

DrugSupplementalUses.defaultProps = {
  IllegalControlledSubstanceUseAnswer: {},
  IllegalControlledSubstanceUseComment: '',
  IllegalControlledSubstanceUseSummaryComment: '',
  List: { items: [], branch: {} },
  onError: (value, arr) => arr,
  section: 'drugs',
  subsection: 'drugs/supplementalusage',
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
  formType: '',

};

export default connectSubsection(DrugSupplementalUses, sectionConfig);
